import {
  useInfiniteQuery,
  type InfiniteData,
  type UseInfiniteQueryResult,
} from '@tanstack/react-query';

import { useApi } from '@/lib/api-context';

import { auditLogsResponseSchema, type AuditLogsResponse } from './types';
import type { QueryFilters } from './use-url-filters';

const PAGE_SIZE = 50;

/** Filtros de la vista como query del endpoint (sin paginación). */
function filterParams(filters: QueryFilters): URLSearchParams {
  const qs = new URLSearchParams();
  if (filters.category) {
    qs.set('category', filters.category);
  }
  if (filters.occurredFrom) {
    qs.set('occurred_from', filters.occurredFrom);
  }
  if (filters.occurredTo) {
    qs.set('occurred_to', filters.occurredTo);
  }
  return qs;
}

/** Filtros para exportar (spec 2026-10-14 §3): los de la vista, sin página ni cursor. */
export function exportSearch(filters: QueryFilters): string {
  return filterParams(filters).toString();
}

function toSearch(filters: QueryFilters, cursor: string | null): string {
  const qs = new URLSearchParams({ limit: String(PAGE_SIZE) });
  for (const [k, v] of filterParams(filters)) {
    qs.set(k, v);
  }
  if (cursor) {
    qs.set('cursor', cursor);
  }
  return qs.toString();
}

interface Options {
  /** Empresa activa: entra al queryKey para no servir la bitácora cacheada de
   *  otra empresa al cambiar. */
  companyId: string | null;
  /** No dispara hasta conocer la empresa activa (garantiza X-Active-Company en
   *  carga fría/deep-link). */
  enabled: boolean;
}

/**
 * Bitácora de auditoría de la empresa activa, paginada por cursor (append).
 * Valida cada página con Zod (ENG-VAL-01): un drift del backend cae al estado de
 * error de la query, no a un throw en render (nunca pantalla blanca).
 */
export function useAuditLog(
  filters: QueryFilters,
  { companyId, enabled }: Options,
): UseInfiniteQueryResult<InfiniteData<AuditLogsResponse>> {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: ['audit-log', companyId, filters],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const raw = await api.get<unknown>(`/api/v1/audit-logs?${toSearch(filters, pageParam)}`);
      return auditLogsResponseSchema.parse(raw);
    },
    getNextPageParam: (last) => last.meta.next_cursor,
    enabled,
  });
}
